import dayjs from 'dayjs';
import { DateFormats } from '../models';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
export const convertTimeToUTC = (hour, weekday) => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const adjustedWeekday = (weekday + 1) % 7;
    const dateInTimezone = dayjs().tz(localTimezone).day(adjustedWeekday).startOf('day').hour(hour).minute(0).second(0);
    const dateInUTC = dateInTimezone.utc();
    const utcWeekday = (dateInUTC.day() + 6) % 7;
    return {
        weekday: utcWeekday,
        hour: dateInUTC.hour(),
    };
};
export const zeroBasedMondayDays = [
    { id: 0, value: 0, label: 'Monday' },
    { id: 1, value: 1, label: 'Tuesday' },
    { id: 2, value: 2, label: 'Wednesday' },
    { id: 3, value: 3, label: 'Thursday' },
    { id: 4, value: 4, label: 'Friday' },
    { id: 5, value: 5, label: 'Saturday' },
    { id: 6, value: 6, label: 'Sunday' },
];
export const convertUTCScheduleToLocal = (weekday, hour) => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcScheduledTime = dayjs()
        .utc()
        .startOf('week')
        .add(1, 'day')
        .add(weekday, 'day')
        .hour(hour)
        .minute(0)
        .second(0);
    const localScheduledTime = utcScheduledTime.tz(userTimezone);
    return {
        weekday: (localScheduledTime.day() + 6) % 7,
        hour: localScheduledTime.hour(),
        formatted: localScheduledTime.format('dddd h:mm A z'),
        timezone: userTimezone,
    };
};
export const getGenericTimezoneLabel = (timezoneAbbreviation, scheduleTimezone) => {
    if (!timezoneAbbreviation || !scheduleTimezone) {
        return '';
    }
    const inTheStates = scheduleTimezone.toLowerCase().startsWith('america/');
    return inTheStates ? timezoneAbbreviation.replace(/[SD]T$/, 'T') : timezoneAbbreviation;
};
export const localDigestFormat = (schedule) => {
    const weekdayNumber = Number(schedule.weekday);
    const hourNumber = Number(schedule.hour);
    const dayIndex = (weekdayNumber + 7) % 7;
    const dayLabel = zeroBasedMondayDays[dayIndex]?.label || '';
    const formattedTime = dayjs().set('hour', hourNumber).set('minute', 0).set('second', 0).format('h:mm A');
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneAbbreviation = dayjs().tz(userTimezone).format('z');
    const genericZone = getGenericTimezoneLabel(timezoneAbbreviation, userTimezone);
    return `${dayLabel} at ${formattedTime} ${genericZone}`;
};
export const getWeekdays = () => {
    return Array.from({ length: 7 }, (_, i) => {
        const day = i + 1;
        return {
            label: dayjs().day(day).format('dddd'),
            value: day,
        };
    });
};
export const getTimes = (increment) => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += increment || 60) {
            times.push({
                label: dayjs().hour(hour).minute(minute).format('h:mm A'),
                value: hour,
            });
        }
    }
    return times;
};
export const getCurrentTime = (increment) => {
    const times = getTimes(increment);
    const now = dayjs();
    let closestTime = times[0];
    let minDiff = Infinity;
    for (const time of times) {
        const timeObject = dayjs(time.label, 'h:mm A');
        const diff = Math.abs(now.diff(timeObject, 'minute'));
        if (diff < minDiff) {
            closestTime = time;
            minDiff = diff;
        }
    }
    return closestTime;
};
export const getCurrentTimeZone = () => {
    const timeZone = dayjs.tz.guess();
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
    });
    const parts = dtf.formatToParts(new Date());
    const abbreviation = parts.find((part) => part.type === 'timeZoneName')?.value;
    return abbreviation || timeZone;
};
export const formatDate = (dateObj, format = DateFormats.FULL_NUMERIC_YEAR_MONTH_DAY) => dayjs(dateObj).format(format);
export const formatDateString = (dateStr, format = DateFormats.FULL_NUMERIC_YEAR_MONTH_DAY) => formatDate(dayjs(dateStr), format);
export const formatValidDateString = (dateStr, format = DateFormats.FULL_NUMERIC_YEAR_MONTH_DAY, parseFormat) => {
    const dayjsDate = dayjs(dateStr, parseFormat);
    if (dayjsDate.isValid()) {
        return formatDate(dayjsDate, format);
    }
    return undefined;
};
export const toIsoString = (fromDateInitializer) => {
    const initDate = fromDateInitializer || Date.now();
    return new Date(initDate).toISOString();
};
